import React from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

/** Top bar of the meeting room: WIZBOX brand + interview context, elapsed/total clock, connection. */
const clock = (s) => {
  if (s == null) return '--:--';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, '0')}`;
};

const Sparkle = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
    <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z"
      fill="url(#wg)" />
    <path d="M19 14l.8 2.6L22 17.4l-2.2.8L19 21l-.8-2.6L16 17.4l2.2-.8L19 14z" fill="#a5b4fc" />
    <defs>
      <linearGradient id="wg" x1="5" y1="2" x2="19" y2="16" gradientUnits="userSpaceOnUse">
        <stop stopColor="#818cf8" /><stop offset="0.5" stopColor="#a78bfa" /><stop offset="1" stopColor="#34d399" />
      </linearGradient>
    </defs>
  </svg>
);

const MeetingHeader = ({ subtitle, elapsed, total, connection = 'connected', audioWarning, audioWarningHidden,
  audioWarningLabel = 'Microphone',
  onShowAudioWarning, onRetryAudio, onUseTyping, recoveryActionLabel = 'Retry', recoveryBusy = false,
  recoveryCanType = true }) => {
  const connected = connection === 'connected';
  return (
    <div className="grid gap-2 px-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center sm:px-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)]">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-200/70 ring-1 ring-black/5 dark:bg-white/5 dark:ring-white/10">
          <Sparkle />
        </span>
        <div className="min-w-0">
          <h1 className="text-lg font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white">WIZBOX</h1>
          {subtitle && (
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
        </div>
      </div>

      {audioWarning && <div role="alert" className="order-3 flex min-w-0 flex-wrap items-center justify-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-center text-xs text-amber-900 sm:col-span-2 sm:row-start-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
        <FiAlertTriangle className="shrink-0" aria-hidden="true" />
        <span>{audioWarning}</span>
        <button type="button" onClick={onRetryAudio} disabled={recoveryBusy}
          className="inline-flex items-center gap-1 font-semibold underline disabled:opacity-40"><FiRefreshCw />{recoveryActionLabel}</button>
        <button type="button" onClick={onUseTyping} disabled={recoveryBusy || !recoveryCanType}
          className="font-semibold underline disabled:opacity-40">Type answer</button>
      </div>}
      {audioWarningHidden && <div className="order-3 flex justify-center sm:col-span-2 sm:row-start-2 lg:col-span-1 lg:col-start-2 lg:row-start-1">
        <button type="button" onClick={onShowAudioWarning} aria-label={`Show ${audioWarningLabel.toLowerCase()} warning`} title={`Show ${audioWarningLabel.toLowerCase()} warning`}
          className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <FiAlertTriangle aria-hidden="true" /> {audioWarningLabel}
        </button>
      </div>}
      <div className="order-2 flex items-center justify-end gap-2 sm:col-start-2 sm:row-start-1 lg:col-start-3">
        <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-black/5 dark:bg-white/5 dark:text-slate-200 dark:ring-white/10">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#a78bfa" strokeWidth="2">
            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" />
          </svg>
          {clock(elapsed)}{total ? ` / ${clock(total)}` : ''}
        </span>
        <span className="hidden sm:flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-black/5 dark:bg-white/5 dark:text-slate-200 dark:ring-white/10">
          <span className={`h-2 w-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`} />
          {connected ? 'Connected' : 'Connecting…'}
        </span>
      </div>
    </div>
  );
};

export default MeetingHeader;
